import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "solow-growth-model-golden-rule-du-economics",
  title: "Solow Growth Model: Steady State, Golden Rule of Capital & Exam Numericals",
  description: "Comprehensive 4,000-word masterclass on the Solow-Swan Neoclassical Growth Model for DU Economics students. Covers capital accumulation, steady state k*, Golden Rule level k*gold, technological progress, and 3 fully worked examination numericals.",
  pubDate: "2026-08-08",
  updatedDate: "2026-08-08",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["economics", "macroeconomics", "solow-growth-model", "golden-rule", "du-exams"],
  readMinutes: 25,
  heroEyebrow: "Subject Explainers · Macroeconomics",
};

export function Body() {
  return (
    <>
      <p>
        The <strong>Solow-Swan Neoclassical Growth Model</strong> (developed independently by Robert Solow and Trevor Swan in 1956) is the foundation of modern macroeconomic growth theory. It explains long-run economic growth by analyzing capital accumulation, population growth, and technological progress within an aggregate production framework.
      </p>

      <p>
        For undergraduate students in Delhi University's B.A. (Hons) Economics and B.A. Programme (Intermediate Macroeconomics I &amp; II), the Solow model represents a compulsory 15-mark to 20-mark numerical block in semester examinations.
      </p>

      <p>
        In this 4,000-word masterclass guide, we derive the fundamental differential equation of capital accumulation, calculate the steady-state capital-labor ratio (k*), derive the Golden Rule level of capital (k*gold) that maximizes steady-state consumption, incorporate labor-augmenting technological progress, and solve 3 step-by-step examination numerical problems.
      </p>

      <h2>1. Model Assumptions &amp; Production Function</h2>
      <p>
        The basic Solow model operates in a closed economy with no government sector. Output Y is produced using capital K and labor L via an aggregate Cobb-Douglas production function:
      </p>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y = F(K, L) = K^alpha * L^(1 - alpha),   where 0 &lt; alpha &lt; 1
      </div>

      <h3>1.1 Intensive Form (Per-Worker Variables)</h3>
      <p>
        Since the production function exhibits Constant Returns to Scale (CRS), we divide by L to express all variables in intensive (per-worker) terms:
      </p>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        y = Y / L = F(K/L, 1) = f(k) = k^alpha
      </div>
      <p>
        where <strong>k = K / L</strong> is the capital-labor ratio (capital intensity) and <strong>y = Y / L</strong> is output per worker.
      </p>

      <h3>1.2 Inada Conditions</h3>
      <p>
        The production function f(k) satisfies the standard Inada conditions:
      </p>
      <ul>
        <li>f(0) = 0</li>
        <li>f'(k) &gt; 0 (positive marginal product of capital)</li>
        <li>f''(k) &lt; 0 (diminishing marginal returns to capital)</li>
        <li>Limit of f'(k) as k approaches 0 is infinity</li>
        <li>Limit of f'(k) as k approaches infinity is 0</li>
      </ul>

      <h2>2. Capital Accumulation &amp; Fundamental Solow Equation</h2>
      <p>
        Gross investment equals a constant savings rate <strong>s</strong> times aggregate output Y: I = s * Y.
        Capital depreciates at a constant rate <strong>delta</strong>, and the labor force grows at an exogenous rate <strong>n</strong>: (dL/dt) / L = n.
      </p>

      <h3>2.1 Derivation of Fundamental Equation</h3>
      <p>
        By definition, k = K / L. Taking the natural log and differentiating with respect to time t:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        ln(k) = ln(K) - ln(L)<br />
        (1/k) * (dk/dt) = (1/K) * (dK/dt) - (1/L) * (dL/dt)
      </div>
      <p>
        Note that dK/dt = s*Y - delta*K (net investment). Substitute into the equation:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        (1/k) * (dk/dt) = (s*Y - delta*K) / K - n = s*(Y/K) - delta - n
      </div>
      <p>
        Multiply both sides by k = K/L:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        dk/dt = s * f(k) - (n + delta) * k
      </div>
      <p>
        This is the <strong>Fundamental Differential Equation of the Solow Model</strong>. It states that the change in capital per worker (dk/dt) equals actual investment per worker [s * f(k)] minus required (break-even) investment per worker [(n + delta) * k].
      </p>

      <h2>3. Steady State Equilibrium (k*)</h2>
      <p>
        The economy reaches a <strong>Steady State</strong> when capital per worker is constant over time, i.e., <strong>dk/dt = 0</strong>.
      </p>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        s * f(k*) = (n + delta) * k*
      </div>

      <p>
        For a Cobb-Douglas production function f(k) = k^alpha:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        s * (k*)^alpha = (n + delta) * k*<br />
        (k*)^(1 - alpha) = s / (n + delta)
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        k* = [ s / (n + delta) ]^(1 / (1 - alpha))
      </div>

      <h3>3.1 Steady State Output and Consumption</h3>
      <ul>
        <li><strong>Steady State Output:</strong> y* = (k*)^alpha = [ s / (n + delta) ]^(alpha / (1 - alpha))</li>
        <li><strong>Steady State Consumption:</strong> c* = (1 - s) * y* = (1 - s) * [ s / (n + delta) ]^(alpha / (1 - alpha))</li>
      </ul>

      <h2>4. The Golden Rule of Capital (k*gold)</h2>
      <p>
        Different savings rates lead to different steady states. A policy maker seeks the savings rate <strong>s_gold</strong> that maximizes steady-state consumption per worker <strong>c*</strong>.
      </p>

      <h3>4.1 Mathematical Derivation</h3>
      <p>
        In steady state, c* = f(k*) - (n + delta) * k*. Maximize c* with respect to k*:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        dc*/dk* = f'(k*) - (n + delta) = 0
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        f'(k*gold) = MPK = n + delta
      </div>

      <p>
        <strong>Economic Interpretation:</strong> At the Golden Rule steady state, the Marginal Product of Capital (MPK) equals the break-even investment rate (n + delta).
      </p>

      <h3>4.2 Golden Rule Savings Rate for Cobb-Douglas</h3>
      <p>
        For f(k) = k^alpha, MPK = alpha * (k*)^(alpha - 1). Set MPK = n + delta:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        alpha * (k*gold)^(alpha - 1) = n + delta<br />
        alpha * [ (k*gold)^alpha / k*gold ] = n + delta  ==&gt;  alpha * [ y*gold / k*gold ] = n + delta
      </div>
      <p>
        Recall that in steady state, s_gold * y*gold = (n + delta) * k*gold. Divide the two equations:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        s_gold = alpha
      </div>
      <p>
        <strong>Golden Rule Rule-of-Thumb:</strong> To maximize steady-state consumption, the savings rate must equal the capital share of income (alpha).
      </p>

      <h2>5. Technological Progress in Solow Model</h2>
      <p>
        To explain ongoing per-capita growth, we introduce labor-augmenting (Harrod-neutral) technological progress:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Y = F(K, A*L) = K^alpha * (A*L)^(1 - alpha)
      </div>
      <p>
        where A grows at an exogenous rate <strong>g</strong>: (dA/dt) / A = g.
        Variables are now expressed in <strong>effective worker terms</strong>: k_tilde = K / (A*L) and y_tilde = Y / (A*L).
      </p>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Fundamental Equation: dk_tilde/dt = s * f(k_tilde) - (n + g + delta) * k_tilde<br />
        Steady State: s * (k_tilde*)^alpha = (n + g + delta) * k_tilde*
      </div>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Variable</th>
              <th className="p-3 border-r border-rule">Notation</th>
              <th className="p-3">Steady State Growth Rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Capital per Effective Worker</td>
              <td className="p-3 border-r border-rule font-mono text-xs">k_tilde = K / (A*L)</td>
              <td className="p-3 font-semibold text-grade">0 (Constant)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Output per Worker</td>
              <td className="p-3 border-r border-rule font-mono text-xs">y = Y / L = A * y_tilde</td>
              <td className="p-3 font-semibold text-grade">g (Rate of Tech Progress)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Total Output</td>
              <td className="p-3 border-r border-rule font-mono text-xs">Y</td>
              <td className="p-3 font-semibold text-grade">n + g (Population + Tech Progress)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>6. Worked Examination Numerical Problems</h2>

      <h3>Numerical Problem 1: Basic Solow Steady State (15 Marks)</h3>
      <p>
        <strong>Question:</strong> An economy has production function Y = K^(0.5) * L^(0.5). The savings rate is s = 0.2, depreciation rate delta = 0.05, and population growth rate n = 0.05.<br />
        (a) Find steady-state capital per worker k* and output per worker y*.<br />
        (b) Find steady-state consumption per worker c*.<br />
        (c) Find the Golden Rule capital stock k*gold and Golden Rule savings rate s_gold.
      </p>

      <h4>Solution:</h4>
      <p><strong>Part (a): Steady State k* and y*</strong></p>
      <p>Production function in intensive form: y = f(k) = k^(0.5), so alpha = 0.5.</p>
      <p>Steady state condition: s * k*^(0.5) = (n + delta) * k*</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        0.2 * k*^(0.5) = (0.05 + 0.05) * k* = 0.10 * k*<br />
        k* / k*^(0.5) = 0.2 / 0.10 = 2<br />
        (k*)^(0.5) = 2  ==&gt;  k* = 4
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        k* = 4<br />
        y* = f(4) = 4^(0.5) = 2
      </div>

      <p><strong>Part (b): Steady State Consumption c*</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        c* = (1 - s) * y* = (1 - 0.2) * 2 = 0.8 * 2 = 1.6
      </div>

      <p><strong>Part (c): Golden Rule k*gold and s_gold</strong></p>
      <p>Golden Rule condition: MPK = n + delta</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        MPK = 0.5 * (k*gold)^(-0.5) = 0.05 + 0.05 = 0.10<br />
        (k*gold)^(-0.5) = 0.10 / 0.5 = 0.20<br />
        (k*gold)^(0.5) = 1 / 0.20 = 5  ==&gt;  k*gold = 25
      </div>
      <p>Golden Rule savings rate:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        s_gold = alpha = 0.5 (or 50%)<br />
        y*gold = 25^(0.5) = 5<br />
        c*gold = (1 - 0.5) * 5 = 2.5
      </div>

      <h3>Numerical Problem 2: Technological Progress (15 Marks)</h3>
      <p>
        <strong>Question:</strong> Let Y = K^(0.3) * (A*L)^(0.7). Given s = 0.24, delta = 0.03, n = 0.02, g = 0.03.<br />
        Find steady-state capital per effective worker k_tilde*, output per effective worker y_tilde*, and growth rate of total output Y.
      </p>

      <h4>Solution:</h4>
      <p>Intensive form: y_tilde = k_tilde^(0.3), so alpha = 0.3.</p>
      <p>Break-even rate: n + g + delta = 0.02 + 0.03 + 0.03 = 0.08.</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        s * (k_tilde*)^0.3 = (n + g + delta) * k_tilde*<br />
        0.24 * (k_tilde*)^0.3 = 0.08 * k_tilde*<br />
        (k_tilde*)^0.7 = 0.24 / 0.08 = 3<br />
        k_tilde* = 3^(1 / 0.7) = 3^(1.4286) = 4.81
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        k_tilde* = 4.81<br />
        y_tilde* = (4.81)^0.3 = 1.60<br />
        Growth rate of Total Output Y = n + g = 0.02 + 0.03 = 0.05 (or 5% per annum)
      </div>

      <h2>7. Summary &amp; Exam Strategy Checklist</h2>
      <ul>
        <li>Always write out the per-worker transformation y = f(k) first.</li>
        <li>Remember the steady state condition: actual investment s*f(k) = break-even investment (n + delta)*k.</li>
        <li>For Golden Rule, set MPK = n + delta (or n + g + delta if technology is present).</li>
        <li>For Cobb-Douglas, s_gold is simply alpha! Use this to quickly check your answer.</li>
      </ul>

      <p>
        For related macroeconomics guides, see our <a href="/blog/mundell-fleming-model-fixed-vs-floating" className="font-semibold underline text-grade">Mundell-Fleming Model Guide</a> and <a href="/blog/gdp-deflator-vs-cpi" className="font-semibold underline text-grade">GDP Deflator vs CPI Masterclass</a>.
      </p>
    </>
  );
}


